import { useEffect, useRef, useState } from 'react'

export default function SecureFlowLoader() {
  const [phase, setPhase] = useState('mount')
  const [hidden, setHidden] = useState(false)
  const [flip, setFlip] = useState(null)
  const brandRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduced) {
      setHidden(true)
      return
    }

    document.body.style.overflow = 'hidden'

    const raf = requestAnimationFrame(() => setPhase('draw'))
    const tFill = setTimeout(() => setPhase('fill'), 2050)

    const tFlip = setTimeout(() => {
      const target = document.querySelector('[data-sf-hero-brand]')
      const source = brandRef.current
      if (target && source) {
        const t = target.getBoundingClientRect()
        const s = source.getBoundingClientRect()
        if (t.width && t.height && s.width && s.height) {
          const dx = t.left + t.width / 2 - (s.left + s.width / 2)
          const dy = t.top + t.height / 2 - (s.top + s.height / 2)
          const scale = Math.min(t.width / s.width, t.height / s.height)
          setFlip({ dx, dy, scale })
        }
      }
      setPhase('dismiss')
    }, 2400)

    const tHide = setTimeout(() => {
      setHidden(true)
      document.body.style.overflow = ''
    }, 4000)

    const safety = setTimeout(() => {
      setHidden(true)
      document.body.style.overflow = ''
    }, 6500)

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(tFill)
      clearTimeout(tFlip)
      clearTimeout(tHide)
      clearTimeout(safety)
      document.body.style.overflow = ''
    }
  }, [])

  if (hidden) return null

  const drawn = phase === 'draw' || phase === 'fill' || phase === 'dismiss'
  const filled = phase === 'fill' || phase === 'dismiss'
  const dismissing = phase === 'dismiss'

  const brandStyle = flip
    ? { transform: `translate(${flip.dx}px, ${flip.dy}px) scale(${flip.scale})` }
    : undefined

  return (
    <div
      className={`sfl-loader${dismissing ? ' sfl-dismiss' : ''}`}
      aria-hidden="true"
      role="presentation"
    >
      <div className="sfl-glow" />
      <div className="sfl-stage">
        <span
          ref={brandRef}
          className={`sfl-brand${drawn ? ' sfl-drawn' : ''}${filled ? ' sfl-filled' : ''}${flip ? ' sfl-flying' : ''}`}
          style={brandStyle}
        >
          <svg
            className="sfl-icon"
            viewBox="0 0 880 625"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="SecureFlow"
          >
            <path
              className="sfl-icon-stroke"
              d="M503.519 123.329L739.387 359.197C751.951 371.761 751.962 392.12 739.412 404.671L529.211 614.871C516.661 627.422 496.302 627.41 483.738 614.846L432.552 563.661L475.479 520.735C513.516 482.698 532.753 445.109 533.192 407.969C534.089 371.288 518.251 336.661 485.678 304.088L467.531 285.941L467.626 285.843L446.982 265.198C432.76 250.976 426.562 236.071 428.387 220.482C429.296 204.893 436.624 190.224 450.372 176.476L503.519 123.329ZM443.649 63.4593L503.519 123.329L521.863 104.985C536.07 90.7783 553.49 83.9099 574.124 84.3796C594.758 84.8494 613.562 93.5715 630.537 110.546L822.084 302.093C828.366 308.375 838.545 308.381 844.82 302.106L874.339 272.587C880.615 266.311 880.609 256.132 874.327 249.85L689.662 65.1843C649.748 25.2711 609.616 4.38647 569.265 2.53047C527.997 0.674006 487.886 19.2224 448.933 58.1758L443.649 63.4593ZM372.683 503.791L432.552 563.661L430.11 566.104C391.156 605.057 351.045 623.606 309.777 621.749C269.426 619.893 229.294 599.009 189.381 559.096L4.71511 374.43C-1.56687 368.148 -1.57241 357.968 4.70275 351.693L34.2217 322.174C40.4969 315.899 50.6765 315.905 56.9585 322.187L248.506 513.734C265.48 530.708 284.284 539.43 304.918 539.9C325.552 540.37 342.972 533.502 357.179 519.295L372.683 503.791ZM372.683 503.791L428.67 447.804C442.418 434.056 449.746 419.387 450.655 403.798C452.481 388.209 446.282 373.303 432.061 359.082L411.416 338.437L411.511 338.339L393.364 320.192C360.791 287.619 344.954 252.992 345.851 216.311C346.289 179.17 365.527 141.582 403.563 103.545L443.649 63.4593L389.62 9.43022C377.056 -3.13374 356.697 -3.14482 344.147 9.40552L133.946 219.606C121.396 232.156 121.407 252.516 133.971 265.08L372.683 503.791Z"
            />
            <path
              className="sfl-icon-fill"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M503.519 123.329L739.387 359.197C751.951 371.761 751.962 392.12 739.412 404.671L529.211 614.871C516.661 627.422 496.302 627.41 483.738 614.846L432.552 563.661L475.479 520.735C513.516 482.698 532.753 445.109 533.192 407.969C534.089 371.288 518.251 336.661 485.678 304.088L467.531 285.941L467.626 285.843L446.982 265.198C432.76 250.976 426.562 236.071 428.387 220.482C429.296 204.893 436.624 190.224 450.372 176.476L503.519 123.329ZM443.649 63.4593L503.519 123.329L521.863 104.985C536.07 90.7783 553.49 83.9099 574.124 84.3796C594.758 84.8494 613.562 93.5715 630.537 110.546L822.084 302.093C828.366 308.375 838.545 308.381 844.82 302.106L874.339 272.587C880.615 266.311 880.609 256.132 874.327 249.85L689.662 65.1843C649.748 25.2711 609.616 4.38647 569.265 2.53047C527.997 0.674006 487.886 19.2224 448.933 58.1758L443.649 63.4593ZM372.683 503.791L432.552 563.661L430.11 566.104C391.156 605.057 351.045 623.606 309.777 621.749C269.426 619.893 229.294 599.009 189.381 559.096L4.71511 374.43C-1.56687 368.148 -1.57241 357.968 4.70275 351.693L34.2217 322.174C40.4969 315.899 50.6765 315.905 56.9585 322.187L248.506 513.734C265.48 530.708 284.284 539.43 304.918 539.9C325.552 540.37 342.972 533.502 357.179 519.295L372.683 503.791ZM372.683 503.791L428.67 447.804C442.418 434.056 449.746 419.387 450.655 403.798C452.481 388.209 446.282 373.303 432.061 359.082L411.416 338.437L411.511 338.339L393.364 320.192C360.791 287.619 344.954 252.992 345.851 216.311C346.289 179.17 365.527 141.582 403.563 103.545L443.649 63.4593L389.62 9.43022C377.056 -3.13374 356.697 -3.14482 344.147 9.40552L133.946 219.606C121.396 232.156 121.407 252.516 133.971 265.08L372.683 503.791Z"
            />
          </svg>
          <span className="sfl-wordmark">SecureFlow</span>
        </span>
      </div>
      <div className="sfl-status">
        <span className="sfl-status-dot" />
        <span>Initializing&nbsp;&middot;&nbsp;SecureFlow</span>
      </div>

      <style jsx>{`
        .sfl-loader {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: #050B19;
          display: grid;
          place-items: center;
          opacity: 1;
          transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
            visibility 0s linear 0.9s;
        }
        .sfl-loader.sfl-dismiss {
          opacity: 0;
          pointer-events: none;
        }
        .sfl-glow {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 120vmin;
          height: 120vmin;
          border-radius: 50%;
          background: radial-gradient(
            closest-side,
            rgba(14, 165, 233, 0.28) 0%,
            rgba(34, 197, 94, 0.10) 40%,
            transparent 70%
          );
          filter: blur(40px);
          pointer-events: none;
        }
        .sfl-stage {
          position: relative;
          display: grid;
          place-items: center;
          z-index: 1;
        }
        .sfl-brand {
          display: inline-flex;
          align-items: center;
          gap: clamp(10px, 1vw, 14px);
          transform-origin: 50% 50%;
          will-change: transform;
        }
        .sfl-brand.sfl-flying {
          transition: transform 1400ms cubic-bezier(0.22, 0.85, 0.25, 1);
        }
        .sfl-icon {
          width: clamp(120px, 14vw, 180px);
          height: auto;
          overflow: visible;
        }
        .sfl-icon-stroke {
          fill: none;
          stroke: #22C55E;
          stroke-width: 18;
          stroke-dasharray: 4400;
          stroke-dashoffset: 4400;
          transition: stroke-dashoffset 1.7s cubic-bezier(0.2, 0.7, 0.2, 1),
            opacity 0.4s ease;
        }
        .sfl-brand.sfl-drawn .sfl-icon-stroke {
          stroke-dashoffset: 0;
        }
        .sfl-brand.sfl-filled .sfl-icon-stroke {
          opacity: 0;
        }
        .sfl-icon-fill {
          fill: #22C55E;
          opacity: 0;
          transition: opacity 0.6s ease;
        }
        .sfl-brand.sfl-drawn .sfl-icon-fill {
          opacity: 1;
          transition-delay: 1.4s;
        }
        .sfl-wordmark {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
          font-size: clamp(36px, 4vw, 56px);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #E8F1FF;
          white-space: nowrap;
          opacity: 0;
          transform: translateX(-12px);
          filter: blur(8px);
          transition: opacity 0.7s ease, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1),
            filter 0.7s ease;
        }
        .sfl-brand.sfl-drawn .sfl-wordmark {
          opacity: 1;
          transform: translateX(0);
          filter: blur(0);
          transition-delay: 1.5s;
        }
        .sfl-status {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
          font-size: 10px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: rgba(232, 241, 255, 0.45);
          z-index: 1;
          transition: opacity 0.4s ease;
        }
        .sfl-loader.sfl-dismiss .sfl-status,
        .sfl-loader.sfl-dismiss .sfl-glow {
          opacity: 0;
        }
        .sfl-status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #38BDF8;
          animation: sflPulse 1.6s ease-in-out infinite;
        }
        @keyframes sflPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sfl-loader { display: none; }
        }
      `}</style>
    </div>
  )
}
